import React from "react";
import { Card, Tag, Flex, theme, Grid } from "antd";
import { DateField } from "@refinedev/antd";
import { MessageTwoTone, ThunderboltOutlined } from "@ant-design/icons";
import { StatusLabels } from "./statusLabels";
import { BaseRecord } from "@refinedev/core";
import { IAutomationTask, AutomationTaskStep, AutomationTaskStatus } from "../../../models/automation-task";
import { OrderStatus } from "../../../models/order";
import { CarMakeLabel } from "../../../shared/components";

const { useToken } = theme;

interface OrderCardProps {
    order: BaseRecord;
    fieldLabels: Record<string, string>;
    onClick: (order: BaseRecord) => void;
    isAdmin?: boolean;
}

export const OrderCard: React.FC<OrderCardProps> = ({ order, fieldLabels, onClick, isAdmin = false }) => {
    const { token } = useToken();
    const breakpoint = Grid.useBreakpoint();
    const isMobile = typeof breakpoint.lg === "undefined" ? false : !breakpoint.lg;

    const hasAvailableAutomation = () => {
        if (!isAdmin || !order?.automationTasks) return false;

        const analyzeTask = order.automationTasks.find((task: IAutomationTask) =>
            task.step === AutomationTaskStep.analyze &&
            task.status === AutomationTaskStatus.completed
        );

        return analyzeTask?.metadata?.analysisResult?.projects &&
            Array.isArray(analyzeTask.metadata.analysisResult.projects) &&
            analyzeTask.metadata.analysisResult.projects.length > 0 &&
            order.status != OrderStatus.completed &&
            order.status != OrderStatus.sav;
    };

    if (isMobile) {
        return (
            <Card
                className="shadow-sm rounded-lg cursor-pointer mb-3 mx-3"
                onClick={() => onClick(order)}
                bodyStyle={{ padding: "12px 16px" }}
                style={{ backgroundColor: token.colorBgElevated }}
            >
                <Flex justify="space-between" align="flex-start" className="mb-2">
                    <Flex align="center" gap={4}>
                        <span className="text-xs font-medium" style={{ color: token.colorTextSecondary }}>
                            #{order.id}
                        </span>
                        <Tag
                            style={{
                                backgroundColor: token.colorBgContainer,
                                color: token.colorPrimary,
                                borderColor: token.colorBorderSecondary,
                                fontSize: 11,
                            }}
                        >
                            <DateField value={order.createdAt} format={"DD MMM HH:mm"} />
                        </Tag>
                    </Flex>
                    <Flex align="center" gap={4}>
                        {order?.unreadMessagesCount > 0 && (
                            <Tag
                                style={{
                                    backgroundColor: token.colorInfoBg,
                                    color: token.colorInfo,
                                    borderColor: token.colorInfoBorder,
                                    fontSize: 11,
                                }}
                            >
                                <Flex align="center" gap={4}>
                                    <MessageTwoTone />
                                    {order.unreadMessagesCount}
                                </Flex>
                            </Tag>
                        )}
                        <StatusLabels value={order.status} />
                    </Flex>
                </Flex>
                <div className="font-semibold text-base mb-1" style={{ color: token.colorText }}>
                    <CarMakeLabel make={order.make} model={order.model} />
                </div>
                <div className="text-sm mb-1" style={{ color: token.colorTextSecondary }}>
                    {order.vin}
                </div>
                {order.products?.length > 0 && (
                    <div className="text-sm mb-1" style={{ color: token.colorTextSecondary }}>
                        {order.products.map((product: any) => product.name).join(", ")}
                    </div>
                )}
                {isAdmin && order.account?.name && (
                    <div className="text-sm" style={{ color: token.colorTextSecondary }}>
                        {order.account.name}
                    </div>
                )}
                {hasAvailableAutomation() && (
                    <Tag color="orange" className="mt-2 font-bold" icon={<ThunderboltOutlined />}>
                        Automatisation
                    </Tag>
                )}
            </Card>
        );
    }

    return (
        <Card
            className="shadow-none rounded-none border-r-0 border-l-0 border-t-0 cursor-pointer transition-colors duration-200"
            onClick={() => onClick(order)}
            bodyStyle={{
                padding: "8px 24px 8px 24px",
                borderBottom: `1px solid ${token.colorBorderSecondary}`,
            }}
            style={{
                backgroundColor: token.colorBgElevated
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = token.colorBgTextHover;
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
            }}
        >
            <div className="px-2 py-2">
                <div className="grid gap-4" style={{ gridTemplateColumns: '80px 120px 1fr 1fr 1fr 1fr 220px' }}>
                    <div className="flex items-center gap-2">
                        <div className="font-medium text-sm" style={{ color: token.colorText }}>
                            {order.id}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="font-medium text-sm" style={{ color: token.colorText }}>
                            <Tag
                                style={{
                                    backgroundColor: token.colorBgElevated,
                                    color: token.colorText,
                                    borderColor: token.colorBorderBg
                                }}
                            >
                                <DateField
                                    className="text-xs"
                                    value={order.createdAt}
                                    format={"DD MMM HH:mm"}
                                    style={{ color: token.colorPrimary }}
                                />
                            </Tag>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="font-medium" style={{ color: token.colorText }}>
                            <CarMakeLabel make={order.make} model={order.model} />
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="font-medium" style={{ color: token.colorText }}>{order.vin}</div>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="font-medium" style={{ color: token.colorText }}>
                            {order.products?.map((product: any) => product.name).join(", ")}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="font-medium" style={{ color: token.colorText }}>{order.account?.name}</div>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="flex items-center">
                            {order?.unreadMessagesCount > 0 &&
                                <Tag
                                    className="font-bold mr-2"
                                    style={{
                                        backgroundColor: token.colorInfoBg,
                                        color: token.colorInfo,
                                        borderColor: token.colorInfoBorder
                                    }}
                                >
                                    <Flex align="center" gap={"small"}>
                                        <MessageTwoTone />
                                        {order.unreadMessagesCount}
                                    </Flex>
                                </Tag>
                            }
                            <StatusLabels value={order.status} />
                            {hasAvailableAutomation() && (
                                <Tag
                                    color="orange"
                                    className="ml-2 font-bold"
                                    icon={<ThunderboltOutlined />}
                                >
                                    Automatisation
                                </Tag>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Card>
    );
}; 